import React, { useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Edit3,
  ExternalLink,
  RefreshCw,
  Sparkles,
  Target,
  X,
} from 'lucide-react';
import { usePlanDisplay } from '../hooks/usePlanDisplay';
import {
  DevelopmentPlan,
  SessionPlan,
  WeeklyTheme,
  PlanEditRequest,
  SessionActivity,
} from '../services/aiIntelligenceService';

interface PlanDisplayProps {
  planId: string;
  onBack: () => void;
}

export function PlanDisplay({ planId, onBack }: PlanDisplayProps) {
  const {
    plan,
    loadingState,
    error,
    editingSection,
    isSaving,
    loadPlan,
    startEditing,
    cancelEditing,
    saveEdit,
  } = usePlanDisplay(planId);

  if (loadingState === 'loading') {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Loading development plan...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadingState === 'error' || !plan) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Unable to Load Plan
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {error || 'Something went wrong while loading the plan.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => loadPlan(planId)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors min-h-[44px]"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header with back button and status */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Plans
        </button>
        <PlanStatusBadge status={plan.status} />
      </div>

      {/* Plan Overview */}
      <PlanOverviewSection
        plan={plan}
        editingSection={editingSection}
        isSaving={isSaving}
        onStartEditing={startEditing}
        onCancelEditing={cancelEditing}
        onSaveEdit={saveEdit}
      />

      {/* Weekly Themes */}
      <WeeklyThemesSection
        themes={plan.weeklyThemes}
        editingSection={editingSection}
        isSaving={isSaving}
        onStartEditing={startEditing}
        onCancelEditing={cancelEditing}
        onSaveEdit={saveEdit}
      />

      {/* Session Plans */}
      <SessionPlansSection
        sessions={plan.sessionPlans}
        recommendations={plan.recommendations}
        editingSection={editingSection}
        isSaving={isSaving}
        onStartEditing={startEditing}
        onCancelEditing={cancelEditing}
        onSaveEdit={saveEdit}
      />

      {/* Recommendations */}
      <RecommendationsSection recommendations={plan.recommendations} />

      {/* Generation Metadata */}
      <MetadataSection plan={plan} />
    </div>
  );
}


/* ─── AI Badge ─────────────────────────────────────────────────────────────── */

function AIBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
      <Sparkles className="w-3 h-3" />
      AI-assisted recommendation
    </span>
  );
}

/* ─── Plan Status Badge ────────────────────────────────────────────────────── */

function PlanStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    generated: 'bg-blue-50 text-blue-700 border-blue-200',
    reviewed: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    accepted: 'bg-green-50 text-green-700 border-green-200',
    regenerated: 'bg-orange-50 text-orange-700 border-orange-200',
    archived: 'bg-gray-50 text-gray-500 border-gray-200',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
        styles[status] || styles.generated
      }`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/* ─── Plan Overview Section ────────────────────────────────────────────────── */

interface PlanOverviewSectionProps {
  plan: DevelopmentPlan;
  editingSection: string | null;
  isSaving: boolean;
  onStartEditing: (key: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

function PlanOverviewSection({
  plan,
  editingSection,
  isSaving,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
}: PlanOverviewSectionProps) {
  const [editSummary, setEditSummary] = useState(plan.overview.summary);
  const [editRationale, setEditRationale] = useState(plan.overview.rationale);
  const isEditing = editingSection === 'overview';

  const handleSave = async () => {
    await onSaveEdit({
      sectionType: 'overview',
      sectionId: 'overview',
      modifiedContent: {
        summary: editSummary,
        keyWeaknesses: plan.overview.keyWeaknesses,
        rationale: editRationale,
        blockDurationWeeks: plan.overview.blockDurationWeeks,
      },
    });
  };

  const handleStartEdit = () => {
    setEditSummary(plan.overview.summary);
    setEditRationale(plan.overview.rationale);
    onStartEditing('overview');
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-900">Plan Overview</h2>
          <AIBadge />
        </div>
        {!isEditing && (
          <button
            onClick={handleStartEdit}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-600 transition-colors min-h-[44px] px-2"
            aria-label="Edit overview"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Summary
            </label>
            <textarea
              value={editSummary}
              onChange={(e) => setEditSummary(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[80px]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AI Rationale
            </label>
            <textarea
              value={editRationale}
              onChange={(e) => setEditRationale(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[80px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-sm font-medium rounded-lg transition-colors min-h-[44px]"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Save
            </button>
            <button
              onClick={onCancelEditing}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors min-h-[44px]"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {plan.overview.summary}
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{plan.overview.blockDurationWeeks}-week development block</span>
          </div>

          {plan.overview.keyWeaknesses.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Key Weaknesses Identified
              </h4>
              <div className="flex flex-wrap gap-2">
                {plan.overview.keyWeaknesses.map((weakness, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200"
                  >
                    {weakness}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-purple-500" />
              AI Rationale
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              {plan.overview.rationale}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}


/* ─── Weekly Themes Section ────────────────────────────────────────────────── */

interface WeeklyThemesSectionProps {
  themes: WeeklyTheme[];
  editingSection: string | null;
  isSaving: boolean;
  onStartEditing: (key: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

function WeeklyThemesSection({
  themes,
  editingSection,
  isSaving,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
}: WeeklyThemesSectionProps) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900">Weekly Themes</h2>
        <AIBadge />
      </div>
      <div className="space-y-4">
        {themes.map((theme) => (
          <WeeklyThemeCard
            key={theme.weekNumber}
            theme={theme}
            editingSection={editingSection}
            isSaving={isSaving}
            onStartEditing={onStartEditing}
            onCancelEditing={onCancelEditing}
            onSaveEdit={onSaveEdit}
          />
        ))}
      </div>
    </section>
  );
}

interface WeeklyThemeCardProps {
  theme: WeeklyTheme;
  editingSection: string | null;
  isSaving: boolean;
  onStartEditing: (key: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

function WeeklyThemeCard({
  theme,
  editingSection,
  isSaving,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
}: WeeklyThemeCardProps) {
  const sectionKey = `theme-${theme.weekNumber}`;
  const isEditing = editingSection === sectionKey;
  const [editFocusArea, setEditFocusArea] = useState(theme.focusArea);
  const [editProgressionLogic, setEditProgressionLogic] = useState(theme.progressionLogic);
  const [editObjectives, setEditObjectives] = useState(theme.coachingObjectives.join('\n'));

  const handleStartEdit = () => {
    setEditFocusArea(theme.focusArea);
    setEditProgressionLogic(theme.progressionLogic);
    setEditObjectives(theme.coachingObjectives.join('\n'));
    onStartEditing(sectionKey);
  };

  const handleSave = async () => {
    await onSaveEdit({
      sectionType: 'weeklyTheme',
      sectionId: String(theme.weekNumber),
      modifiedContent: {
        weekNumber: theme.weekNumber,
        focusArea: editFocusArea,
        progressionLogic: editProgressionLogic,
        coachingObjectives: editObjectives.split('\n').filter((o) => o.trim()),
      },
    });
  };

  if (isEditing) {
    return (
      <div className="border border-green-200 rounded-lg p-4 bg-green-50/30">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Week {theme.weekNumber} — Editing
        </h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Focus Area
            </label>
            <input
              type="text"
              value={editFocusArea}
              onChange={(e) => setEditFocusArea(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Progression Logic
            </label>
            <textarea
              value={editProgressionLogic}
              onChange={(e) => setEditProgressionLogic(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[60px]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Coaching Objectives (one per line)
            </label>
            <textarea
              value={editObjectives}
              onChange={(e) => setEditObjectives(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[60px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-xs font-medium rounded-lg transition-colors min-h-[36px]"
            >
              {isSaving ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-3 h-3" />
              )}
              Save
            </button>
            <button
              onClick={onCancelEditing}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors min-h-[36px]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-100 rounded-lg p-4 hover:border-gray-200 transition-colors group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
              Week {theme.weekNumber}
            </span>
            <h4 className="text-sm font-semibold text-gray-900">
              {theme.focusArea}
            </h4>
          </div>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            {theme.progressionLogic}
          </p>
          {theme.coachingObjectives.length > 0 && (
            <ul className="mt-2 space-y-1">
              {theme.coachingObjectives.map((obj, idx) => (
                <li key={idx} className="text-xs text-gray-500 flex items-start gap-1.5">
                  <Target className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                  {obj}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={handleStartEdit}
          className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-green-600 transition-all min-h-[44px] px-2"
          aria-label={`Edit week ${theme.weekNumber} theme`}
        >
          <Edit3 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}


/* ─── Session Plans Section ────────────────────────────────────────────────── */

interface SessionPlansSectionProps {
  sessions: SessionPlan[];
  recommendations: DevelopmentPlan['recommendations'];
  editingSection: string | null;
  isSaving: boolean;
  onStartEditing: (key: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

function SessionPlansSection({
  sessions,
  recommendations,
  editingSection,
  isSaving,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
}: SessionPlansSectionProps) {
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  const toggleSession = (sessionId: string) => {
    setExpandedSession((prev) => (prev === sessionId ? null : sessionId));
  };

  // Group sessions by week
  const sessionsByWeek = sessions.reduce<Record<number, SessionPlan[]>>((acc, session) => {
    if (!acc[session.weekNumber]) {
      acc[session.weekNumber] = [];
    }
    acc[session.weekNumber].push(session);
    return acc;
  }, {});

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900">Session Plans</h2>
        <AIBadge />
      </div>

      <div className="space-y-6">
        {Object.entries(sessionsByWeek)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([weekNum, weekSessions]) => (
            <div key={weekNum}>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                Week {weekNum}
              </h3>
              <div className="space-y-2">
                {weekSessions
                  .sort((a, b) => a.sessionNumber - b.sessionNumber)
                  .map((session) => (
                    <SessionPlanCard
                      key={session.sessionId}
                      session={session}
                      recommendations={recommendations}
                      isExpanded={expandedSession === session.sessionId}
                      onToggle={() => toggleSession(session.sessionId)}
                      editingSection={editingSection}
                      isSaving={isSaving}
                      onStartEditing={onStartEditing}
                      onCancelEditing={onCancelEditing}
                      onSaveEdit={onSaveEdit}
                    />
                  ))}
              </div>
            </div>
          ))}
      </div>
    </section>
  );
}

interface SessionPlanCardProps {
  session: SessionPlan;
  recommendations: DevelopmentPlan['recommendations'];
  isExpanded: boolean;
  onToggle: () => void;
  editingSection: string | null;
  isSaving: boolean;
  onStartEditing: (key: string) => void;
  onCancelEditing: () => void;
  onSaveEdit: (edit: PlanEditRequest) => Promise<boolean>;
}

function SessionPlanCard({
  session,
  recommendations,
  isExpanded,
  onToggle,
  editingSection,
  isSaving,
  onStartEditing,
  onCancelEditing,
  onSaveEdit,
}: SessionPlanCardProps) {
  const sectionKey = `session-${session.sessionId}`;
  const isEditing = editingSection === sectionKey;
  const [editTitle, setEditTitle] = useState(session.title);
  const [editObjective, setEditObjective] = useState(session.objective);
  const [editCoachingPoints, setEditCoachingPoints] = useState(
    session.coachingPoints.join('\n')
  );

  const sessionDrills = recommendations.drills.filter(
    (d) => d.sessionId === session.sessionId
  );
  const sessionVideos = recommendations.videos.filter(
    (v) => v.sessionId === session.sessionId
  );

  const handleStartEdit = () => {
    setEditTitle(session.title);
    setEditObjective(session.objective);
    setEditCoachingPoints(session.coachingPoints.join('\n'));
    onStartEditing(sectionKey);
  };

  const handleSave = async () => {
    await onSaveEdit({
      sectionType: 'sessionPlan',
      sectionId: session.sessionId,
      modifiedContent: {
        ...session,
        title: editTitle,
        objective: editObjective,
        coachingPoints: editCoachingPoints.split('\n').filter((p) => p.trim()),
      },
    });
  };

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      {/* Session header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left min-h-[44px]"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          <div>
            <span className="text-sm font-medium text-gray-900">
              Session {session.sessionNumber}: {session.title}
            </span>
            <span className="ml-2 text-xs text-gray-500">
              {session.objective}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {session.durationMinutes}min
          </span>
        </div>
      </button>

      {/* Expanded session detail */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50/50">
          {isEditing ? (
            <SessionEditForm
              editTitle={editTitle}
              editObjective={editObjective}
              editCoachingPoints={editCoachingPoints}
              isSaving={isSaving}
              onTitleChange={setEditTitle}
              onObjectiveChange={setEditObjective}
              onCoachingPointsChange={setEditCoachingPoints}
              onSave={handleSave}
              onCancel={onCancelEditing}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={handleStartEdit}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-600 transition-colors min-h-[44px] px-2"
                  aria-label={`Edit session ${session.sessionNumber}`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Edit
                </button>
              </div>

              {/* Warm-up */}
              <ActivityBlock label="Warm-up" activity={session.warmUp} />

              {/* Main Practice */}
              <ActivityBlock label="Main Practice" activity={session.mainPractice} />

              {/* Conditioned Game */}
              <ActivityBlock label="Conditioned Game" activity={session.conditionedGame} />

              {/* Coaching Points */}
              {session.coachingPoints.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">
                    Coaching Points
                  </h5>
                  <ul className="space-y-1">
                    {session.coachingPoints.map((point, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-gray-600 flex items-start gap-1.5"
                      >
                        <span className="text-green-500 mt-0.5">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Progressions & Regressions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {session.progressions.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-1">
                      Progressions
                    </h5>
                    <ul className="space-y-1">
                      {session.progressions.map((p, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="text-blue-500 mt-0.5">↑</span>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {session.regressions.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-1">
                      Regressions
                    </h5>
                    <ul className="space-y-1">
                      {session.regressions.map((r, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="text-orange-500 mt-0.5">↓</span>
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Linked Drills & Videos */}
              {(sessionDrills.length > 0 || sessionVideos.length > 0) && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-purple-500" />
                    Linked Resources
                  </h5>
                  <div className="space-y-2">
                    {sessionDrills.map((drill, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-xs text-gray-600 bg-white rounded p-2 border border-gray-100"
                      >
                        <Target className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-gray-700">
                            {drill.title}
                          </span>
                          <span className="text-gray-400 ml-1">
                            — {drill.focusArea}
                          </span>
                        </div>
                      </div>
                    ))}
                    {sessionVideos.map((video, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-xs text-gray-600 bg-white rounded p-2 border border-gray-100"
                      >
                        <ExternalLink className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-gray-700">
                            {video.title}
                          </span>
                          {video.url && (
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-1 text-blue-500 hover:underline"
                            >
                              Watch
                            </a>
                          )}
                          <span className="text-gray-400 ml-1">
                            — {video.relevance}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Activity Block ───────────────────────────────────────────────────────── */

function ActivityBlock({
  label,
  activity,
}: {
  label: string;
  activity: SessionActivity;
}) {
  return (
    <div>
      <h5 className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-2">
        {label}
        <span className="text-xs font-normal text-gray-400">
          ({activity.durationMinutes}min)
        </span>
      </h5>
      <p className="text-xs text-gray-600 leading-relaxed">
        {activity.description}
      </p>
      {activity.setup && (
        <p className="text-xs text-gray-500 mt-1 italic">
          Setup: {activity.setup}
        </p>
      )}
      {activity.keyCoachingPoints && activity.keyCoachingPoints.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {activity.keyCoachingPoints.map((point, idx) => (
            <li key={idx} className="text-xs text-gray-500 flex items-start gap-1">
              <span className="text-green-400">•</span>
              {point}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── Session Edit Form ────────────────────────────────────────────────────── */

interface SessionEditFormProps {
  editTitle: string;
  editObjective: string;
  editCoachingPoints: string;
  isSaving: boolean;
  onTitleChange: (val: string) => void;
  onObjectiveChange: (val: string) => void;
  onCoachingPointsChange: (val: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function SessionEditForm({
  editTitle,
  editObjective,
  editCoachingPoints,
  isSaving,
  onTitleChange,
  onObjectiveChange,
  onCoachingPointsChange,
  onSave,
  onCancel,
}: SessionEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Title
        </label>
        <input
          type="text"
          value={editTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Objective
        </label>
        <input
          type="text"
          value={editObjective}
          onChange={(e) => onObjectiveChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Coaching Points (one per line)
        </label>
        <textarea
          value={editCoachingPoints}
          onChange={(e) => onCoachingPointsChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[80px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-xs font-medium rounded-lg transition-colors min-h-[36px]"
        >
          {isSaving ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <CheckCircle className="w-3 h-3" />
          )}
          Save
        </button>
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors min-h-[36px]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


/* ─── Recommendations Section ──────────────────────────────────────────────── */

interface RecommendationsSectionProps {
  recommendations: DevelopmentPlan['recommendations'];
}

function RecommendationsSection({ recommendations }: RecommendationsSectionProps) {
  const { drills, videos } = recommendations;

  if (drills.length === 0 && videos.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900">Recommendations</h2>
        <AIBadge />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {drills.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-green-500" />
              Drill Recommendations
            </h3>
            <div className="space-y-2">
              {drills.map((drill, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <p className="text-sm font-medium text-gray-800">
                    {drill.title}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {drill.description}
                  </p>
                  <span className="inline-block mt-1.5 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                    {drill.focusArea}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {videos.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-500" />
              Video Recommendations
            </h3>
            <div className="space-y-2">
              {videos.map((video, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium text-gray-800">
                      {video.title}
                    </p>
                    {video.url && (
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-500 hover:underline flex items-center gap-1 flex-shrink-0 ml-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Watch
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {video.relevance}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Metadata Section ─────────────────────────────────────────────────────── */

interface MetadataSectionProps {
  plan: DevelopmentPlan;
}

function MetadataSection({ plan }: MetadataSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const meta = plan.generationMetadata;

  return (
    <section className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left min-h-[44px]"
        aria-expanded={isExpanded}
      >
        <span className="text-xs font-medium text-gray-500">
          Generation Details
        </span>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <p className="text-xs text-gray-400">Model</p>
            <p className="text-xs font-medium text-gray-700">{meta.model}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Prompt Version</p>
            <p className="text-xs font-medium text-gray-700">
              {meta.promptVersion}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Duration</p>
            <p className="text-xs font-medium text-gray-700">
              {(meta.durationMs / 1000).toFixed(1)}s
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Tokens Used</p>
            <p className="text-xs font-medium text-gray-700">
              {meta.totalTokens.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Generated</p>
            <p className="text-xs font-medium text-gray-700">
              {new Date(plan.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Schema Version</p>
            <p className="text-xs font-medium text-gray-700">
              {plan.schemaVersion}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Status</p>
            <p className="text-xs font-medium text-gray-700">
              {plan.status}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Edits</p>
            <p className="text-xs font-medium text-gray-700">
              {plan.editHistory.length}
            </p>
          </div>
          {meta.validationWarnings.length > 0 && (
            <div className="col-span-2 sm:col-span-4">
              <p className="text-xs text-gray-400 mb-1">Validation Warnings</p>
              <ul className="space-y-0.5">
                {meta.validationWarnings.map((w, idx) => (
                  <li key={idx} className="text-xs text-yellow-600">
                    ⚠ {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
